import React from 'react';
import Label from 'models/LabelModel';
import axios from 'helpers/axios';
import config from 'helpers/config';
import { Trash2, Edit3, Plus, Tag } from 'lucide-react';
import { displayConfirmationModal } from 'helpers/services/removeConfirmationService';

interface Props {
    labelType: { title: string; value: string };
}

interface State {
    labels: Label[];
    isCreateNewLabelActive: boolean;
    title: string | null;
    color: string | null;
    editableLabelId: string | null;
}

class LabelGroupList extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            labels: [],
            isCreateNewLabelActive: false,
            title: null,
            color: null,
            editableLabelId: null,
        };

        this.handleChange = this.handleChange.bind(this);
        this.createNewLabel = this.createNewLabel.bind(this);
        this.updateLabel = this.updateLabel.bind(this);
    }

    componentDidMount() {
        axios
            .get(`${config.apiUrl}/label/${this.props.labelType.value}`)
            .then((res) => this.setState({ labels: res.data }));
    }

    createNewLabel() {
        const { title, color } = this.state;
        const label = {
            subject: this.props.labelType.value,
            title,
            color: !!color ? color : '#000',
        };
        axios
            .post(`${config.apiUrl}/label/`, label)
            .then((_) =>
                this.setState({ title: null, color: null, isCreateNewLabelActive: false }, () =>
                    this.componentDidMount(),
                ),
            );
    }

    updateLabel() {
        const { title, color, editableLabelId } = this.state;
        const label = {
            title,
            color: !!color ? color : '#000',
            id: editableLabelId,
        };
        axios
            .put(`${config.apiUrl}/label`, label)
            .then((_) =>
                this.setState({ title: null, color: null, editableLabelId: null }, () => this.componentDidMount()),
            );
    }
    handleChange(e: React.FormEvent<HTMLInputElement>) {
        const { name, value } = e.currentTarget;
        this.setState({ [name]: value } as any);
    }
    editLabel(_: React.MouseEvent, label: Label) {
        this.setState({
            editableLabelId: label.id,
            color: label.color,
            title: label.title,
            isCreateNewLabelActive: false,
        });
    }

    handleDelete(label: Label) {
        displayConfirmationModal(`label: "${label.title}"`)
            .then(() => {
                axios.delete(`${config.apiUrl}/label/${label.id}`).then(() => {
                    this.componentDidMount();
                    this.setState({ title: null, color: null, editableLabelId: null }, () => this.componentDidMount());
                });
            })
            .catch((_) => _);
    }

    getTextColor = (bgColor: string) => {
        // Simple contrast calculation - in reality you'd want a more sophisticated algorithm
        const hex = bgColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 128 ? '#000000' : '#FFFFFF';
    };

    render() {
        const { labels, isCreateNewLabelActive, editableLabelId } = this.state;

        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Section Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <Tag className="h-5 w-5 text-gray-500 mr-3" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {this.props.labelType.title} Labels
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Manage color-coded labels for {this.props.labelType.title.toLowerCase()}{' '}
                                    organization
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() =>
                                this.setState({
                                    isCreateNewLabelActive: !isCreateNewLabelActive,
                                    editableLabelId: null,
                                    title: null,
                                    color: null,
                                })
                            }
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                        >
                            <Plus className="h-4 w-4" />
                            <span>Add Label</span>
                        </button>
                    </div>
                </div>

                {/* Labels List */}
                <div className="divide-y divide-gray-200">
                    {/* Create New Label Form */}
                    {isCreateNewLabelActive && (
                        <div className="p-6 bg-blue-50 border-l-4 border-l-blue-500">
                            <h4 className="text-md font-semibold text-blue-900 mb-4">Create New Label</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Label Text</label>
                                    <input
                                        type="text"
                                        name="title"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        onChange={this.handleChange}
                                        value={this.state.title || ''}
                                        placeholder="Enter label text"
                                        autoFocus
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Color</label>
                                    <div className="flex items-center space-x-3">
                                        <input
                                            type="color"
                                            name="color"
                                            onChange={this.handleChange}
                                            value={this.state.color || '#000000'}
                                            className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                        />
                                        <span className="text-sm text-gray-500">{this.state.color || '#000000'}</span>
                                    </div>
                                </div>
                                <div className="flex space-x-2 justify-end">
                                    <button
                                        onClick={() =>
                                            this.setState({
                                                isCreateNewLabelActive: false,
                                                color: null,
                                                title: null,
                                            })
                                        }
                                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-semibold rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                    >
                                        <span>Cancel</span>
                                    </button>
                                    <button
                                        onClick={this.createNewLabel}
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-semibold rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                    >
                                        <span>Save</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Existing Labels */}
                    {labels.length === 0 && !isCreateNewLabelActive ? (
                        <div className="p-12 text-center">
                            <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No labels yet</h3>
                            <p className="text-gray-500 mb-6">
                                Create your first {this.props.labelType.title.toLowerCase()} label to get started.
                            </p>
                            <button
                                onClick={() =>
                                    this.setState({
                                        isCreateNewLabelActive: true,
                                        editableLabelId: null,
                                        title: null,
                                        color: null,
                                    })
                                }
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 mx-auto transition-colors"
                            >
                                <Plus className="h-4 w-4" />
                                <span>Add First Label</span>
                            </button>
                        </div>
                    ) : (
                        labels.map((label, index) => (
                            <div key={index} className="p-6">
                                {label.id === editableLabelId ? (
                                    // Edit Mode
                                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                        <h4 className="text-md font-semibold text-yellow-900 mb-4">Edit Label</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Label Text
                                                </label>
                                                <input
                                                    type="text"
                                                    name="title"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                                                    onChange={this.handleChange}
                                                    value={this.state.title || ''}
                                                    autoFocus
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                    Color
                                                </label>
                                                <div className="flex items-center space-x-3">
                                                    <input
                                                        type="color"
                                                        name="color"
                                                        onChange={this.handleChange}
                                                        value={this.state.color || '#000000'}
                                                        className="w-12 h-10 rounded-lg border border-gray-300 cursor-pointer"
                                                    />
                                                    <span className="text-sm text-gray-500">
                                                        {this.state.color || '#000000'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={() =>
                                                        this.setState({
                                                            editableLabelId: null,
                                                            color: null,
                                                            title: null,
                                                        })
                                                    }
                                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-semibold rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                >
                                                    <span>Cancel</span>
                                                </button>
                                                <button
                                                    onClick={this.updateLabel}
                                                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-semibold rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                                >
                                                    <span>Save</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Display Mode
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <div
                                                className="w-6 h-6 rounded-full border border-gray-300"
                                                style={{ backgroundColor: label.color }}
                                            ></div>
                                            <div>
                                                <div
                                                    className="px-3 py-1 rounded-full text-sm font-semibold border shadow-sm w-fit"
                                                    style={{
                                                        backgroundColor: label.color,
                                                        color: this.getTextColor(label.color),
                                                        borderColor:
                                                            label.color === '#FFFFFF' ? '#E5E7EB' : label.color,
                                                    }}
                                                >
                                                    {label.title}
                                                </div>
                                                {/* <span className="text-lg font-semibold text-gray-900">{label.title}</span> */}
                                                <p className="text-sm text-gray-500">Color: {label.color}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={(e: React.MouseEvent) => this.editLabel(e, label)}
                                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                title="Edit label"
                                            >
                                                <Edit3 className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => this.handleDelete(label)}
                                                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                title="Delete label"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    }
}

export default LabelGroupList;
